import React, { useCallback, useMemo, useState } from 'react';
import {
	isVariant,
	BASE_PRECISION_EXP,
	BigNum,
	BN,
	convertToNumber,
	MarketType,
	PositionDirection,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION,
	QUOTE_PRECISION_EXP,
	TEN_THOUSAND,
	ZERO,
	SpotMarketConfig,
} from '@drift-labs/sdk';
import { Info } from 'react-feather';
import { PencilIcon } from '@heroicons/react/outline';
import { PlusIcon } from '@heroicons/react/solid';
import { useRouter } from 'next/router';
import BuySellToggle from 'src/components/Buttons/BuySellToggle';
import WalletIcon from 'src/components/Icons/WalletIcon';
import SwitchInput from 'src/components/Inputs/SwitchInput';
import Utility from 'src/components/Inputs/Utility';
import Slider from 'src/components/Slider';
import SliderButtonComboInput from 'src/components/SliderButtonComboInput/SliderButtonComboInput';
import Text from 'src/components/Text/Text';
import { marketPriceIncs } from 'src/environmentVariables/EnvironmentVariables';
import useAccountData from 'src/hooks/useAccountData';
import useAccountExists from 'src/hooks/useAccountExists';
import useAccountTargetSpotBalance from 'src/hooks/useAccountTargetSpotBalance';
import useDriftActions from 'src/hooks/useDriftActions';
import useLocalStorageState from 'src/hooks/useLocalStorageState';
import useSafePush from 'src/hooks/useSafePush';
import NumLib from 'src/utils/NumLib';
import UI_UTILS from 'src/utils/uiUtils';
import useCurrentSettings from '../../hooks/useCurrentSettings';
import useUserAccountIsReady from '../../hooks/useUserAccountIsReady';
import useWalletIsConnected from '../../hooks/useWalletIsConnected';
import useDriftStore from '../../stores/DriftStore/useDriftStore';
import Button from '../Button';
import OnboardingTooltip from '../Tooltip/OnboardingTooltip';
import { TradeModal } from '../TradeModal';
import AdvancedPerpTradeSettings from './AdvancedTradeSettings';
import AdvancedSpotTradeSettings from './AdvancedSpotTradeSettings';
import BracketOrderForm, { TriggerOrderInfo } from './BracketOrderForm';
import InfoMessage from './InfoMessage';
import SlippageTolerance from './SlippageTolerance';
import TradeFormInputLabel from './TradeFormInputLabel';
import useTradeFormInputStateEngine from './useTradeFormInputStateEngine';
import { useSpotTradeFormStateEngine } from './useTradeFormStateEngine';
import { usePerpTradeFormStateEngine } from './useTradeFormStateEngine';
import usePriceForOracleOffsetOrder from 'src/hooks/usePriceForOracleOffsetOrders';
import useMaxMarginRatioAndLeverage from 'src/hooks/useMaxMarginRatioAndLeverage';
import { UI_ORDER_TYPES_LIST, UIMarket } from '@drift/common';
import PriceDivergenceWarningMessage from '../PriceDivergenceWarningMessage';
import SettingsSwitch from '../Settings/SettingsSwitch';
import { useWindowSizeStore } from 'src/stores/useWindowSizeStore';
import {
	OrderTypeSelector,
	postOnlyAndReduceOnlyNotAllowedTooltip,
	postOnlyTooltip,
	reduceOnlyTooltip,
	SecondaryPriceBox,
	SizeSelector,
} from './TradeFormPieces';
import useIsLiteMode from 'src/hooks/useIsLiteMode';
import {
	EstEntryPriceLite,
	LeverageLabelLite,
	SizeSelectorLite,
} from './TradeFormLitePieces';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';

const PerpTradeForm = () => {
	const isliteMode = useIsLiteMode();
	const isMobileScreenSize = useIsMobileScreenSize();
	const tradeFormState = usePerpTradeFormStateEngine();

	// This hook keeps the input values in the drift store in sync
	useTradeFormInputStateEngine();

	const set = useDriftStore((s) => s.set);
	const connected = useWalletIsConnected();
	const selectedMarketId = useDriftStore(
		(s) => s.selectedMarket.current.marketId
	);
	const selectedMarketSymbol = useDriftStore(
		(s) => s.selectedMarket.current.symbol
	);
	const selectedMarketBaseAssetSymbol = useDriftStore((s) =>
		s.selectedMarket.current.baseAssetSymbol()
	);
	const currentAccount = useAccountData();
	const maxMarginRatio = useMaxMarginRatioAndLeverage()[0];

	const tradeFormStoreState = useDriftStore((s) => s.tradeForm);

	const actions = useDriftActions();

	const router = useRouter();
	const safePush = useSafePush();

	const setCurrentOrderType = (orderType: string) =>
		set((s) => {
			//@ts-ignore
			s.tradeForm.orderType = orderType;
		});

	const [savedSettings, setSavedSettings] = useCurrentSettings();
	const showTradeConfirmation =
		savedSettings.showTradeConfirmation && !isliteMode;

	const userAccountIsReady = useUserAccountIsReady();

	const accountExists = useAccountExists();

	const oraclePrice = usePriceForOracleOffsetOrder(selectedMarketId);

	const updateShowBracketOrderForm = useCallback((newValue: boolean) => {
		set((s) => {
			s.tradeForm.showBracketOrderForm = newValue;
		});
	}, []);

	const [isSendingTransaction, setIsSendingTransaction] = useState(false);

	// Only used for lite mode to show/hide the form
	const [isLiteModeTpSlExpanded, setIsLiteModeTpSlExpanded] = useState(false);

	const hideOrderForm = useCallback(
		() => updateShowBracketOrderForm(false),
		[]
	);

	const currentPositionSize = NumLib.formatBn.fromQuote(
		tradeFormState.currentPosition?.quoteAssetAmount ?? new BN(0)
	);

	const currentPositionBazeSize = BigNum.from(
		(tradeFormState.currentPosition?.baseAssetAmount ?? new BN(0)).abs(),
		BASE_PRECISION_EXP
	);

	const currentPositionBazeSizeString =
		currentPositionBazeSize.toTradePrecision();

	const totalCollateral =
		currentAccount?.marginInfo?.totalCollateral ?? new BN(0);
	const freeCollateral =
		currentAccount?.marginInfo?.freeCollateral ?? new BN(0);

	const totalCurrentPositionSize = NumLib.formatBn.fromQuote(
		totalCollateral
			.sub(freeCollateral)
			.mul(new BN(tradeFormState.maxLeverageAvailable))
	);

	const currentLeverage = currentAccount?.marginInfo?.leverage ?? 0;

	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);

	const currentMarketSymbol = selectedMarketSymbol;
	const formattedMarketSymbol = selectedMarketBaseAssetSymbol;

	//const minSizeInc = marketSizeIncs[formattedMarketSymbol] ?? 0.01;
	const minPriceInc = marketPriceIncs[formattedMarketSymbol] ?? 0.01;

	const toggleReduceOnly = (updatedReduceOnly: boolean) => {
		if (updatedReduceOnly && rawBaseValue.gt(currentPositionBazeSize)) {
			handleFormChangeValue(currentPositionBazeSize.toTradePrecision(), 'base');
		}

		set((s) => {
			s.tradeForm.reduceOnly = updatedReduceOnly;
		});
	};
	const togglePostOnly = () => {
		if (!postOnly) {
			set((s) => {
				s.tradeForm.immediateOrCancel = false;
			});
		}

		set((s) => {
			s.tradeForm.postOnly = !s.tradeForm.postOnly;
		});
	};
	const _toggleIOC = () => {
		if (!immediateOrCancel) {
			set((s) => {
				s.tradeForm.postOnly = false;
			});
		}

		set((s) => {
			s.tradeForm.immediateOrCancel = !s.tradeForm.immediateOrCancel;
		});
	};

	const {
		side,
		leadSide,
		quoteSizeStringValue,
		baseSizeStringValue,
		reduceOnly,
		postOnly,
		immediateOrCancel,
		stepSize,
	} = useDriftStore((s) => ({
		side: s.tradeForm.side,
		quoteSizeStringValue: s.tradeForm.quoteSizeStringValue,
		baseSizeStringValue: s.tradeForm.baseSizeStringValue,
		leadSide: s.tradeForm.leadSide,
		slippageTolerance: s.tradeForm.slippageTolerance,
		reduceOnly: s.tradeForm.reduceOnly,
		postOnly: s.tradeForm.postOnly,
		immediateOrCancel: s.tradeForm.immediateOrCancel,
		stepSize: s.tradeForm.stepSize,
	}));

	const displayPrecision = `${stepSize}`.split('.')[1]?.length ?? 2;

	const rawQuoteValue = Number(quoteSizeStringValue);
	const bigNumQuoteValue = BigNum.fromPrint(
		quoteSizeStringValue.toString(),
		QUOTE_PRECISION_EXP
	);
	const rawBaseValue = BigNum.fromPrint(
		baseSizeStringValue.toString(),
		BASE_PRECISION_EXP
	);

	const maxBaseSizeString =
		tradeFormState.maxBaseAvailable.toFixed(displayPrecision);

	let leverageAfterTrade;
	try {
		leverageAfterTrade =
			userAccountIsReady && currentAccount
				? convertToNumber(
						currentAccount.client.accountLeverageRatioAfterTrade(
							selectedMarketId.marketIndex,
							MarketType.PERP,
							NumLib.formatNum.toRawBn(rawQuoteValue, QUOTE_PRECISION),
							side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT
						),
						TEN_THOUSAND
				  )
				: 0;
	} catch (e) {
		console.error(e);
		leverageAfterTrade = 0;
	}

	const { priceBoxStringValue, secondaryPriceBoxStringValue } = useDriftStore(
		(s) => s.tradeForm
	);

	const setBaseFormValue = (newBaseSize: string) => {
		const rounded = UI_UTILS.roundToStepSizeIfLargeEnough(
			newBaseSize,
			stepSize
		);

		set((s) => {
			s.tradeForm.baseSizeStringValue = rounded;
		});
	};

	const setQuoteFormValue = (newQuoteSize: string) =>
		set((s) => {
			s.tradeForm.quoteSizeStringValue = newQuoteSize;
		});

	const setPriceBoxStringValue = (newValue: string) => {
		set((s) => {
			s.tradeForm.priceBoxStringValue = newValue;
		});
	};

	const setSecondaryPriceBoxStringValue = useCallback(
		(newValue: string) =>
			set((s) => {
				s.tradeForm.secondaryPriceBoxStringValue = newValue;
			}),
		[]
	);

	const addBracketOrdersCallback = useCallback(
		(
			bracketOrders: {
				takeProfit: TriggerOrderInfo;
				stopLoss: TriggerOrderInfo;
			},
			keepFormOpen = false
		) => {
			if (!keepFormOpen) {
				updateShowBracketOrderForm(false);
			}
			set((s) => {
				s.tradeForm.bracketOrders = bracketOrders;
			});
		},
		[]
	);

	const [showTradeModal, setShowTradeModal] = useState(false);
	const [hasAgreedToPriceDivergence, setHasAgreedToPriceDivergence] =
		useState(false);

	async function onSubmit() {
		try {
			setIsSendingTransaction(true);

			if (!connected) {
				set((s) => {
					s.wallet.lastRouteBeforeConnect = router.asPath;
				});
				safePush('/onboarding/connect');
				return;
			}

			if (showTradeConfirmation) {
				setShowTradeModal(true);
				return;
			} else {
				await actions.openPerpTradeFormOrder({
					...tradeFormStoreState,
					targetMarketIndex: selectedMarketId.marketIndex,
					oraclePrice: oraclePrice.val,
					currentSettings: savedSettings,
				});
			}
		} finally {
			setIsLiteModeTpSlExpanded(false);
			setIsSendingTransaction(false);
		}
	}

	const setLeadSide = useCallback((side: 'quote' | 'base') => {
		set((s) => {
			s.tradeForm.leadSide = side;
		});
	}, []);

	const setSide = useCallback((side: 'buy' | 'sell') => {
		set((s) => {
			s.tradeForm.side = side;
			s.tradeForm.closingPosition = false;
		});
	}, []);

	/**
	 * Using the same values to render in the field and use as the current value state causes the fields to act as if over-sanitised.
	 * This function handles the value coming back from the tradeforms on change, which will be the string representation of a number, and updates the relevant parts of the state to match.
	 */
	const handleFormChangeValue = (
		value: string,
		fieldName: 'base' | 'quote' | 'percentage' | 'price',
		turnOffClosePosition = true
	) => {
		// dont allow negative values for any fields
		if (value.charAt(0) === '-') {
			value = '0';
		}

		if (value === '') {
			setBaseFormValue('');
			setQuoteFormValue('');
			if (fieldName === 'price') {
				setPriceBoxStringValue('');
			}
			return;
		}

		// don't allow non-number characters
		if (isNaN(parseInt(value.replace('.', '')))) {
			return;
		}

		// Don't allow entering a value longer than 10 digits because it makes the UI look strange. A user needing to type more than 10 digits into either of the size fields is unrealistic anyway
		value = value.slice(0, 10);

		if (turnOffClosePosition) {
			set((s) => {
				s.tradeForm.closingPosition = false;
			});
		}

		switch (fieldName) {
			case 'price': {
				const price = BigNum.fromPrint(value, QUOTE_PRECISION_EXP);

				if (leadSide === 'base') {
					if (price.eqZero()) {
						setQuoteFormValue('');
					} else {
						setQuoteFormValue(price.scalarMul(rawBaseValue).toFixed(2));
					}
				}

				if (leadSide === 'quote') {
					if (price.eqZero()) {
						setQuoteFormValue('');
					} else {
						setBaseFormValue(
							bigNumQuoteValue
								.scale(QUOTE_PRECISION, price.val)
								.toTradePrecision()
						);
					}
				}

				setPriceBoxStringValue(value);
				break;
			}
			case 'base': {
				setLeadSide('base');
				setBaseFormValue(value);
				break;
			}
			case 'quote': {
				setLeadSide('quote');
				setQuoteFormValue(value);
				break;
			}
			case 'percentage': {
				const numericalValue = Math.max(parseFloat(value ?? '0'), 0);
				const percentage = Math.min(Math.max(numericalValue, 0), 100);

				// If current leverage is 1x, slider will already have percentage 20%, but that *actually* means that we're not trying to take any quote size ... adjust for this
				const currentLeverage =
					currentPositionSize /
					((tradeFormState.maxQuoteAvailable + currentPositionSize) / 5);

				const currentLeveragePercentage =
					(currentLeverage / tradeFormState.maxLeverageAvailable) * 100;

				// "available percentage" isn't 100% if we already have leverage .. e.g. if we're already at 50% leverage, only 50% of the slider is available ... adjust quote amount for that
				const availablePercentage = 100 - currentLeveragePercentage;
				const newQuoteSize = Math.max(
					((percentage - currentLeveragePercentage) / availablePercentage) *
						tradeFormState.maxQuoteAvailable,
					0
				);
				setLeadSide('quote');
				setQuoteFormValue(newQuoteSize.toFixed(2));
				break;
			}
		}

		return;
	};

	const windowHeight = useWindowSizeStore((s) => s.heightString);
	const windowIsTall = windowHeight === 'tall';

	const onButtonChange = useCallback((side) => {
		setSide(side);
	}, []);

	const onOrderTypeChange = useCallback((newValue) => {
		setCurrentOrderType(newValue);
	}, []);

	const orderTypeOptions = useMemo(() => {
		return UI_ORDER_TYPES_LIST.filter(
			(orderType) => orderType.value !== 'oracle'
		);
	}, [UI_ORDER_TYPES_LIST]);

	const onPriceBoxChange = useCallback((value) => {
		setPriceBoxStringValue(value);
	}, []);

	const onSecondaryPriceBoxChange = useCallback((value) => {
		setSecondaryPriceBoxStringValue(value);
	}, []);

	const onBaseSizeChange = useCallback(
		(value) => {
			handleFormChangeValue(value, 'base');
		},
		[stepSize]
	);

	const handleQuoteSizeChange = useCallback(
		(value) => handleFormChangeValue(value, 'quote'),
		[]
	);

	const handleToggleShowConfirmation = useCallback(() => {
		setSavedSettings({
			showTradeConfirmation: !showTradeConfirmation,
		});
	}, [showTradeConfirmation]);

	const handleSliderChange = useCallback(
		(v) =>
			handleFormChangeValue(
				(Math.floor(v * 10000) / 10000).toFixed(2),
				'quote'
			),
		[]
	);

	const setMaxLevSelected = useCallback(
		(newSelection: boolean) =>
			set((s) => {
				s.tradeForm.maxLeverageSelected = newSelection;
			}),
		[]
	);

	const saveLimitPrice = useCallback(
		(value: string) => {
			if (tradeFormState.orderType !== 'limit') return;
			set((s) => {
				s.tradeForm.savedLimitPrice = isNaN(Number(value))
					? undefined
					: {
							market: selectedMarketId,
							price: Number(value),
					  };
			});
		},
		[tradeFormState.orderType, selectedMarketId?.marketIndex]
	);

	const handleToggleLiteModeTpSlExpanded = useCallback(() => {
		setIsLiteModeTpSlExpanded(!isLiteModeTpSlExpanded);
	}, [isLiteModeTpSlExpanded]);

	// slider stays focused when mouse leaves the tradeform while dragging - this fixes by unfocusing it when you re-enter
	const unFocus = () => {
		//@ts-ignore
		if (document.activeElement.role == 'slider') {
			//@ts-ignore
			document.activeElement.blur();
		}
	};

	const submitButton = connected ? (
		<Button.BigSemantic
			positive={side === 'buy'}
			disabled={
				tradeFormState.tradeButtonIsDisabled ||
				(tradeFormState.showPriceEstimateOracleDivergenceWarning &&
					!showTradeConfirmation &&
					!hasAgreedToPriceDivergence) ||
				(isGeoblocked && !reduceOnly) ||
				isSendingTransaction
			}
			onClick={onSubmit}
		>
			{tradeFormState.marketInSettlement ? (
				<Text.H3
					className={`flex items-center justify-center whitespace-normal`}
				>
					{`${currentMarketSymbol} IN SETTLEMENT`}
				</Text.H3>
			) : (
				<Text.H3
					className={`flex items-center justify-center whitespace-normal`}
				>
					{`${side === 'buy' ? 'LONG' : 'SHORT'} ${
						rawBaseValue.gt(ZERO)
							? `${
									leadSide != 'base' ? `~` : ``
							  }${UI_UTILS.roundToStepSizeIfLargeEnough(
									baseSizeStringValue,
									stepSize
							  )}`
							: ''
					} ${currentMarketSymbol}`}
				</Text.H3>
			)}
		</Button.BigSemantic>
	) : (
		<Button.Secondary size="LARGE" onClick={onSubmit} disabled={isGeoblocked}>
			{isGeoblocked ? (
				'Unavailable'
			) : (
				<>
					<WalletIcon className="self-center mr-2" fill="var(--text-default)" />
					<span className="self-center mt-0.5"> Connect Wallet </span>
				</>
			)}
		</Button.Secondary>
	);

	// Different rendering for lite mode
	if (isliteMode && !isMobileScreenSize) {
		return (
			<div onMouseLeave={unFocus}>
				<BuySellToggle isPerp={true} onChange={onButtonChange} />

				<SizeSelectorLite
					baseStepSize={stepSize}
					quoteStepSize={0.01}
					baseSizeStringValue={baseSizeStringValue}
					quoteSizeStringValue={quoteSizeStringValue}
					maxQuote={tradeFormState.maxQuoteAvailable}
					maxBase={tradeFormState.maxBaseAvailable}
					handleChangeSizeBase={onBaseSizeChange}
					handleChangeSizeQuote={handleQuoteSizeChange}
				/>

				<div className="my-5">
					<LeverageLabelLite />
					<div className="h-10 pt-4">
						<SliderButtonComboInput
							currentLeverage={currentLeverage}
							targetTradeQuoteSize={rawQuoteValue}
							totalCurrentPositionSize={totalCurrentPositionSize}
							targetMarketCurrentPositionSize={currentPositionSize}
							sameSide={
								!tradeFormState.currentPosition?.baseAssetAmount ||
								tradeFormState.currentPosition?.baseAssetAmount.eq(ZERO) ||
								(tradeFormState.currentPosition?.baseAssetAmount?.isNeg()
									? 'sell'
									: 'buy') === side
							}
							maxQuoteSize={tradeFormState.maxQuoteAvailable}
							maxLeverage={tradeFormState.maxLeverageAvailable}
							onCurrentSizeChange={handleSliderChange}
							leverageAfterTrade={leverageAfterTrade}
							setMaxLevSelected={setMaxLevSelected}
							showButtons={false}
							largeFont={true}
							disabled={!connected}
						/>
					</div>
				</div>

				<EstEntryPriceLite
					value={BigNum.from(
						tradeFormStoreState.priceImpact.entryPrice,
						PRICE_PRECISION_EXP
					)?.toNum()}
					warn={tradeFormState.showPriceEstimateOracleDivergenceWarning}
				/>

				<BracketOrderForm
					direction={tradeFormStoreState.side == 'buy' ? 'long' : 'short'}
					entryPrice={
						tradeFormState.orderType == 'market'
							? BigNum.from(
									tradeFormStoreState.priceImpact.entryPrice,
									PRICE_PRECISION_EXP
							  )
							: BigNum.fromPrint(priceBoxStringValue, PRICE_PRECISION_EXP)
					}
					entrySize={BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP)}
					goBackCallback={hideOrderForm}
					addOrdersCallback={addBracketOrdersCallback}
					savedTakeProfitPrice={
						tradeFormStoreState.bracketOrders?.takeProfit
							? tradeFormStoreState.bracketOrders.takeProfit.price
							: undefined
					}
					savedStopLossPrice={
						tradeFormStoreState.bracketOrders?.stopLoss
							? tradeFormStoreState.bracketOrders.stopLoss.price
							: undefined
					}
					orderType={tradeFormState.orderType}
					assetSymbol={selectedMarketBaseAssetSymbol}
					displayPrecision={displayPrecision}
					isLiteMode={isliteMode}
					isExpanded={isLiteModeTpSlExpanded}
					onToggleExpanded={handleToggleLiteModeTpSlExpanded}
				/>

				{tradeFormState.shouldShowMessage && (
					<div className="my-5">
						<InfoMessage
							type={tradeFormState.messageType}
							message={tradeFormState.message}
							link={tradeFormState.messageLink}
							linkDescription={tradeFormState.messageLinkDescription}
							messageTitle={tradeFormState.messageTitle}
							messageId={tradeFormState.messageId}
						/>
					</div>
				)}

				{tradeFormState.showPriceEstimateOracleDivergenceWarning && (
					<PriceDivergenceWarningMessage
						hasAgreedToPriceDivergence={hasAgreedToPriceDivergence}
						setHasAgreedToPriceDivergence={setHasAgreedToPriceDivergence}
					/>
				)}

				<hr className="border-container-border my-5" />

				<div className="my-5">
					<AdvancedPerpTradeSettings orderType={tradeFormState.orderType} />
				</div>

				{submitButton}
			</div>
		);
	}

	return !tradeFormStoreState.showBracketOrderForm ? (
		<>
			<div
				id="trade_form"
				className={`flex flex-col h-full overflow-auto thin-scroll pt-[1px] ${
					windowIsTall ? `justify-start` : `justify-between`
				}`}
				onMouseLeave={unFocus}
			>
				<div className="flex flex-col">
					<BuySellToggle isPerp={true} onChange={onButtonChange} />
					<Utility.VERTSPACERL />
					<OrderTypeSelector
						orderType={tradeFormState.orderType}
						priceBoxStringValue={priceBoxStringValue}
						minPriceInc={minPriceInc}
						orderTypeOptions={orderTypeOptions}
						onOrderTypeChange={onOrderTypeChange}
						onPriceBoxChange={onPriceBoxChange}
						onPriceBoxSave={saveLimitPrice}
						{...tradeFormState}
					/>
					<Utility.VERTSPACERL />
					{tradeFormState.showSecondaryPriceBox && (
						<SecondaryPriceBox
							secondaryPriceBoxStringValue={secondaryPriceBoxStringValue}
							minPriceInc={minPriceInc}
							onSecondaryPriceBoxChange={onSecondaryPriceBoxChange}
						/>
					)}
					<SizeSelector
						reduceOnly={reduceOnly}
						baseSizeStringValue={UI_UTILS.roundToStepSizeIfLargeEnough(
							baseSizeStringValue,
							stepSize
						)}
						formattedMarketSymbol={formattedMarketSymbol}
						quoteSizeStringValue={quoteSizeStringValue}
						maxQuoteAvailable={tradeFormState.maxQuoteAvailable}
						maxBaseAvailableString={maxBaseSizeString}
						showMaxButton={tradeFormState.showMaxButton}
						currentPositionBazeSizeString={currentPositionBazeSizeString}
						onBaseSizeChange={onBaseSizeChange}
						handleQuoteSizeChange={handleQuoteSizeChange}
						stepSize={stepSize}
						userHasSelfImposedMax={!!maxMarginRatio && maxMarginRatio !== 0}
						orderType={tradeFormState.orderType}
					/>

					{tradeFormState.showLeverageSlider && (
						<>
							<Utility.VERTSPACERS />
							<div className="font-display">
								<SliderButtonComboInput
									currentLeverage={currentLeverage}
									targetTradeQuoteSize={rawQuoteValue}
									totalCurrentPositionSize={totalCurrentPositionSize}
									targetMarketCurrentPositionSize={currentPositionSize}
									sameSide={
										!tradeFormState.currentPosition?.baseAssetAmount ||
										tradeFormState.currentPosition?.baseAssetAmount.eq(ZERO) ||
										(tradeFormState.currentPosition?.baseAssetAmount?.isNeg()
											? 'sell'
											: 'buy') === side
									}
									maxQuoteSize={tradeFormState.maxQuoteAvailable}
									maxLeverage={tradeFormState.maxLeverageAvailable}
									title="Leverage"
									onCurrentSizeChange={handleSliderChange}
									leverageAfterTrade={leverageAfterTrade}
									setMaxLevSelected={setMaxLevSelected}
									disabled={!connected}
								/>
							</div>
						</>
					)}

					{tradeFormState.showReducePositionSlider && (
						<>
							<Utility.VERTSPACERS />
							<div className="flex justify-between w-full mb-2">
								<TradeFormInputLabel>{'Position Size (%)'}</TradeFormInputLabel>
							</div>
							<Slider
								type="percent"
								disabled={null}
								value={parseFloat(
									currentPositionBazeSize.eqZero()
										? '0'
										: rawBaseValue.toPercentage(currentPositionBazeSize, 4)
								)}
								onDrop={(val) => {
									handleFormChangeValue(
										currentPositionBazeSize.scale(val, 100).printShort(true),
										'base'
									);
								}}
								onMove={(val) => {
									handleFormChangeValue(
										currentPositionBazeSize.scale(val, 100).printShort(true),
										'base'
									);
								}}
								step={1}
								maxButtonTransition={false}
								max={100}
								min={0}
							/>
						</>
					)}

					{tradeFormState.showSlippageTolerance && (
						<>
							<Utility.VERTSPACERL />
							<div className="block">
								<SlippageTolerance />
							</div>
						</>
					)}

					{(tradeFormState.showReduceOnly || tradeFormState.showPostOnly) && (
						<>
							<Utility.VERTSPACERM />
							<div className="flex gap-2">
								{tradeFormState.showReduceOnly && (
									<SwitchInput
										label="Reduce Only"
										checked={reduceOnly && !postOnly}
										onChange={() => toggleReduceOnly(!reduceOnly)}
										tooltip={
											postOnly
												? postOnlyAndReduceOnlyNotAllowedTooltip
												: reduceOnlyTooltip
										}
										disabled={postOnly && tradeFormState.orderType != 'market'}
									/>
								)}
								{tradeFormState.showPostOnly && (
									<SwitchInput
										label="Post"
										checked={postOnly && !reduceOnly}
										onChange={togglePostOnly}
										tooltip={
											reduceOnly
												? postOnlyAndReduceOnlyNotAllowedTooltip
												: postOnlyTooltip
										}
										disabled={reduceOnly}
									/>
								)}
							</div>
						</>
					)}

					{(tradeFormState.orderType == 'market' ||
						(tradeFormState.orderType == 'limit' &&
							!tradeFormStoreState.reduceOnly)) &&
						accountExists &&
						!isGeoblocked && (
							<>
								{!tradeFormStoreState.bracketOrders ? (
									<>
										<Utility.VERTSPACERS />
										<Button.Ghost
											size="MEDIUM"
											className="inline-flex w-[60%] text-xs pl-0"
											onClick={() => updateShowBracketOrderForm(true)}
											disabled={
												!tradeFormStoreState.priceImpact.entryPrice ||
												tradeFormStoreState.priceImpact.entryPrice.eq(ZERO) ||
												(tradeFormStoreState.orderType !== 'market' &&
													!tradeFormStoreState.priceBoxStringValue)
											}
											borderRadius=".5rem"
											textClass="text-text-default"
										>
											<PlusIcon width={'16px'} />
											<span className="ml-1">Add TP/SL Orders</span>
										</Button.Ghost>
									</>
								) : (
									<>
										<div className="flex flex-col w-full mt-2 space-y-1">
											<div className="inline-flex justify-between w-full text-xs">
												<Text.BODY3 className="text-text-label">
													Trigger Orders
												</Text.BODY3>
												<div
													className={
														'text-text-label hover:cursor-pointer inline-flex'
													}
													onClick={() => updateShowBracketOrderForm(true)}
												>
													<PencilIcon className="mr-0.5" width={'16px'} />
													<Text.BODY3>Edit</Text.BODY3>
												</div>
											</div>
											{tradeFormStoreState.bracketOrders.takeProfit && (
												<div className="inline-flex justify-between w-full text-xs">
													<Text.BODY3 className="text-positive-green">
														Take Profit
													</Text.BODY3>
													<Text.BODY3 className={'text-text-emphasis'}>
														{tradeFormStoreState.bracketOrders.takeProfit.price.toNotional()}
													</Text.BODY3>
												</div>
											)}
											{tradeFormStoreState.bracketOrders.stopLoss && (
												<div className="inline-flex justify-between w-full text-xs">
													<Text.BODY3 className="text-negative-red">
														Stop Loss
													</Text.BODY3>
													<Text.BODY3 className={'text-text-emphasis'}>
														{tradeFormStoreState.bracketOrders.stopLoss.price.toNotional()}
													</Text.BODY3>
												</div>
											)}
										</div>
										<Utility.VERTSPACERS />
									</>
								)}
							</>
						)}

					{tradeFormState.shouldShowMessage && (
						<>
							<div className="pb-2" />
							<InfoMessage
								type={tradeFormState.messageType}
								message={tradeFormState.message}
								link={tradeFormState.messageLink}
								linkDescription={tradeFormState.messageLinkDescription}
								messageTitle={tradeFormState.messageTitle}
								messageId={tradeFormState.messageId}
							/>

							<div className="pb-2" />
						</>
					)}
				</div>

				<div
					className={`flex flex-col justify-center ${windowIsTall && `mt-12`}`}
				>
					<AdvancedPerpTradeSettings orderType={tradeFormState.orderType} />

					<Utility.VERTSPACERS />

					<div className="flex items-left">
						<SettingsSwitch
							label="Show Confirmation"
							checked={showTradeConfirmation}
							onChange={handleToggleShowConfirmation}
							spacedOut
							includeLineSpace={false}
							className="w-full [&>span]:text-[12px]"
						/>
					</div>

					<Utility.VERTSPACERS />

					{tradeFormState.showPriceEstimateOracleDivergenceWarning &&
						!showTradeConfirmation && (
							<PriceDivergenceWarningMessage
								hasAgreedToPriceDivergence={hasAgreedToPriceDivergence}
								setHasAgreedToPriceDivergence={setHasAgreedToPriceDivergence}
							/>
						)}

					<Utility.VERTSPACERM />

					{submitButton}
				</div>
			</div>

			{showTradeModal && (
				<TradeModal
					onClose={() => {
						setShowTradeModal(false);
					}}
					tradeFormMessage={
						tradeFormState.skipMessageInConfirmationModal
							? ''
							: tradeFormState.message
					}
					tradeFormMessageType={tradeFormState.messageType}
				/>
			)}
		</>
	) : (
		<BracketOrderForm
			direction={tradeFormStoreState.side == 'buy' ? 'long' : 'short'}
			entryPrice={
				tradeFormState.orderType == 'market'
					? BigNum.from(
							tradeFormStoreState.priceImpact.entryPrice,
							PRICE_PRECISION_EXP
					  )
					: BigNum.fromPrint(priceBoxStringValue, PRICE_PRECISION_EXP)
			}
			entrySize={BigNum.fromPrint(baseSizeStringValue, BASE_PRECISION_EXP)}
			goBackCallback={hideOrderForm}
			addOrdersCallback={addBracketOrdersCallback}
			savedTakeProfitPrice={
				tradeFormStoreState.bracketOrders?.takeProfit
					? tradeFormStoreState.bracketOrders.takeProfit.price
					: undefined
			}
			savedStopLossPrice={
				tradeFormStoreState.bracketOrders?.stopLoss
					? tradeFormStoreState.bracketOrders.stopLoss.price
					: undefined
			}
			orderType={tradeFormState.orderType}
			assetSymbol={selectedMarketBaseAssetSymbol}
			displayPrecision={displayPrecision}
		/>
	);
};

const SpotTradeForm = () => {
	const tradeFormState = useSpotTradeFormStateEngine();

	// This hook keeps the input values in the drift store in sync
	useTradeFormInputStateEngine();

	const set = useDriftStore((s) => s.set);
	const connected = useWalletIsConnected();
	const selectedUiMarket = useDriftStore((s) => s.selectedMarket.current);
	const selectedMarket = selectedUiMarket.market;
	const currentAccount = useAccountData();
	const maxMarginRatio = useMaxMarginRatioAndLeverage()[0];
	const isDelegateAccount = currentAccount?.isDelegatedTo;

	const tradeFormStoreState = useDriftStore((s) => s.tradeForm);
	const userAccountIsReady = useUserAccountIsReady();
	const oraclePrice = usePriceForOracleOffsetOrder(selectedUiMarket.marketId);
	const [hasAgreedToPriceDivergence, setHasAgreedToPriceDivergence] =
		useState(false);

	const actions = useDriftActions();

	const setCurrentOrderType = (orderType: string) =>
		set((s) => {
			//@ts-ignore
			s.tradeForm.orderType = orderType;
		});

	const [savedSettings, setSavedSettings] = useCurrentSettings();
	const { showTradeConfirmation } = savedSettings;

	const userHasMarginEnabled = currentAccount?.marginEnabled ?? false;

	const [showMarginTradingMessage, setShowMarginTradingMessage] =
		useLocalStorageState(
			'showMarginTradingMessage',
			!userHasMarginEnabled ? true : false
		);
	const [isSendingTransaction, setIsSendingTransaction] = useState(false);

	const currentPosition = useAccountTargetSpotBalance(
		selectedMarket as SpotMarketConfig,
		currentAccount?.userKey ?? ''
	);

	const currentPositionSize =
		currentPosition?.netQuoteValue?.toNum() ?? undefined;

	const currentPositionBaseSize =
		currentPosition?.netBaseBalance?.abs() ?? undefined;

	const currentPositionDirection =
		currentPositionBaseSize?.gtZero() ?? true ? 'long' : 'short';

	const basePrecisionToUse = (selectedMarket as SpotMarketConfig).precisionExp;

	const currentPositionBazeSizeString =
		currentPositionBaseSize.toTradePrecision();

	const totalCollateral =
		currentAccount?.marginInfo?.totalCollateral ?? new BN(0);
	const freeCollateral =
		currentAccount?.marginInfo?.freeCollateral ?? new BN(0);

	const totalCurrentPositionSize = NumLib.formatBn.fromQuote(
		totalCollateral
			.sub(freeCollateral)
			.mul(new BN(tradeFormState.maxLeverageAvailable))
	);

	const currentLeverage = currentAccount?.marginInfo?.leverage ?? 0;

	const isGeoblocked = useDriftStore((s) => s.isGeoblocked);

	const currentMarketSymbol = selectedUiMarket.symbol;
	const formattedMarketSymbol = selectedUiMarket.baseAssetSymbol();
	const minPriceInc = marketPriceIncs[formattedMarketSymbol] ?? 0.01;

	const toggleReduceOnly = (updatedReduceOnly: boolean) => {
		if (!reduceOnly) {
			if (rawBaseValue.gt(currentPositionBaseSize)) {
				handleFormChangeValue(
					currentPositionBaseSize.toTradePrecision(),
					'base'
				);
			}
		}
		// disable this for now, so user can make order post only
		// else if (side === 'sell' && !userHasMarginEnabled) {
		// 	// cant turn off reduce only if selling and margin disabled, ensure it's on
		// 	set((s) => {
		// 		s.tradeForm.reduceOnly = true;
		// 	});

		// 	return;
		// }

		set((s) => {
			s.tradeForm.reduceOnly = updatedReduceOnly;
		});
	};
	const togglePostOnly = () => {
		if (!postOnly) {
			set((s) => {
				s.tradeForm.immediateOrCancel = false;
			});
		}

		set((s) => {
			s.tradeForm.postOnly = !s.tradeForm.postOnly;
		});
	};

	const {
		side,
		leadSide,
		quoteSizeStringValue,
		baseSizeStringValue,
		reduceOnly,
		postOnly,
		stepSize,
		//immediateOrCancel,
	} = useDriftStore((s) => ({
		side: s.tradeForm.side,
		quoteSizeStringValue: s.tradeForm.quoteSizeStringValue,
		baseSizeStringValue: s.tradeForm.baseSizeStringValue,
		leadSide: s.tradeForm.leadSide,
		slippageTolerance: s.tradeForm.slippageTolerance,
		reduceOnly: s.tradeForm.reduceOnly,
		postOnly: s.tradeForm.postOnly,
		stepSize: s.tradeForm.stepSize,
		//immediateOrCancel: s.tradeForm.immediateOrCancel,
	}));

	const rawQuoteValue = Number(quoteSizeStringValue);
	const bigNumQuoteValue = BigNum.fromPrint(
		quoteSizeStringValue,
		QUOTE_PRECISION_EXP
	);
	const rawBaseValue = BigNum.fromPrint(
		baseSizeStringValue,
		basePrecisionToUse
	);

	const displayPrecision = `${stepSize}`.split('.')[1]?.length ?? 2;

	const maxBaseSizeString =
		tradeFormState.maxBaseAvailable.toFixed(displayPrecision);

	let leverageAfterTrade;
	try {
		leverageAfterTrade =
			userAccountIsReady && currentAccount
				? convertToNumber(
						currentAccount.client.accountLeverageRatioAfterTrade(
							selectedMarket.marketIndex,
							MarketType.SPOT,
							NumLib.formatNum.toRawBn(rawQuoteValue, QUOTE_PRECISION),
							side === 'buy' ? PositionDirection.LONG : PositionDirection.SHORT
						),
						TEN_THOUSAND
				  )
				: 0;
	} catch (e) {
		console.error(e);
		leverageAfterTrade = 0;
	}

	const { priceBoxStringValue, secondaryPriceBoxStringValue } = useDriftStore(
		(s) => s.tradeForm
	);

	const setShowConnectWalletModal = (newShowWalletState: boolean) => {
		set((s) => {
			s.modals.showWalletConnectionModal = newShowWalletState;
		});
	};

	const setBaseFormValue = (newBaseSize: string) => {
		const rounded = UI_UTILS.roundToStepSizeIfLargeEnough(
			newBaseSize,
			stepSize
		);

		set((s) => {
			s.tradeForm.baseSizeStringValue = rounded;
		});
	};

	const setQuoteFormValue = (newQuoteSize: string) =>
		set((s) => {
			s.tradeForm.quoteSizeStringValue = newQuoteSize;
		});

	const setPriceBoxStringValue = (newValue: string) => {
		set((s) => {
			s.tradeForm.priceBoxStringValue = newValue;
		});
	};

	const setSecondaryPriceBoxStringValue = useCallback(
		(newValue: string) =>
			set((s) => {
				s.tradeForm.secondaryPriceBoxStringValue = newValue;
			}),
		[]
	);

	const setMaxLevSelected = (newSelection: boolean) =>
		set((s) => {
			s.tradeForm.maxLeverageSelected = newSelection;
		});

	const [showTradeModal, setShowTradeModal] = useState(false);

	async function onSubmit() {
		try {
			setIsSendingTransaction(true);
			if (!connected) {
				setShowConnectWalletModal(true);
				return;
			}

			if (showTradeConfirmation) {
				setShowTradeModal(true);
				return;
			} else
				await actions.openSpotTradeFormOrder({
					...tradeFormStoreState,
					targetMarketIndex: selectedMarket.marketIndex,
					currentPositionBaseSize: currentPositionBaseSize?.val ?? ZERO,
					currentPositionDirection,
					oraclePrice: oraclePrice.val,
					currentSettings: savedSettings,
				});
		} finally {
			setIsSendingTransaction(false);
		}
	}

	const setLeadSide = (side: 'quote' | 'base') => {
		set((s) => {
			s.tradeForm.leadSide = side;
		});
	};

	const setSide = (side: 'buy' | 'sell') => {
		set((s) => {
			s.tradeForm.side = side;
			s.tradeForm.closingPosition = false;
		});
	};

	/**
	 * Using the same values to render in the field and use as the current value state causes the fields to act as if over-sanitised.
	 * This function handles the value coming back from the tradeforms on change, which will be the string representation of a number, and updates the relevant parts of the state to match.
	 */
	const handleFormChangeValue = (
		value: string,
		fieldName: 'base' | 'quote' | 'percentage' | 'price',
		turnOffClosePosition = true
	) => {
		// dont allow negative values for any fields
		if (value.charAt(0) === '-') {
			value = '0';
		}

		if (value === '') {
			setBaseFormValue('');
			setQuoteFormValue('');
			if (fieldName === 'price') {
				setPriceBoxStringValue('');
			}
			return;
		}

		// don't allow non-number characters
		if (isNaN(parseInt(value.replace('.', '')))) {
			return;
		}

		// Don't allow entering a value longer than 10 digits because it makes the UI look strange. A user needing to type more than 10 digits into either of the size fields is unrealistic anyway
		value = value.slice(0, 10);

		if (turnOffClosePosition) {
			set((s) => {
				s.tradeForm.closingPosition = false;
			});
		}

		switch (fieldName) {
			case 'price': {
				const price = BigNum.fromPrint(value, QUOTE_PRECISION_EXP);

				if (leadSide === 'base') {
					if (price.eqZero()) {
						setQuoteFormValue('');
					} else {
						setQuoteFormValue(price.scalarMul(rawBaseValue).toFixed(2));
					}
				}

				if (leadSide === 'quote') {
					if (price.eqZero()) {
						setQuoteFormValue('');
					} else {
						setBaseFormValue(
							bigNumQuoteValue
								.scale(QUOTE_PRECISION, price.val)
								.toTradePrecision()
						);
					}
				}

				setPriceBoxStringValue(value);
				break;
			}
			case 'base': {
				setLeadSide('base');
				setBaseFormValue(value);
				break;
			}
			case 'quote': {
				setLeadSide('quote');
				setQuoteFormValue(value);
				break;
			}
			case 'percentage': {
				// leverage always 0 so pass direct quote/base value
				if (
					!userHasMarginEnabled ||
					tradeFormState.maxLeverageAvailable === 0
				) {
					setLeadSide('quote');
					setQuoteFormValue('');
					break;
				}

				const numericalValue = Math.max(parseFloat(value ?? '0'), 0);
				const percentage = Math.min(Math.max(numericalValue, 0), 100);

				// If current leverage is 1x, slider will already have percentage 20%, but that *actually* means that we're not trying to take any quote size ... adjust for this
				// const currentLeverage =
				// 	currentPositionSize /
				// 	((tradeFormState.maxQuoteAvailable + currentPositionSize) / 5);

				const currentLeveragePercentage =
					(currentLeverage / tradeFormState.maxLeverageAvailable) * 100;

				// "available percentage" isn't 100% if we already have leverage .. e.g. if we're already at 50% leverage, only 50% of the slider is available ... adjust quote amount for that
				const availablePercentage = 100 - currentLeveragePercentage;
				const newQuoteSize = Math.max(
					((percentage - currentLeveragePercentage) / availablePercentage) *
						tradeFormState.maxQuoteAvailable,
					0
				);
				setLeadSide('quote');
				setQuoteFormValue(newQuoteSize.toFixed(2));
				break;
			}
		}

		return;
	};

	const windowHeight = useWindowSizeStore((s) => s.heightString);
	const windowIsTall = windowHeight === 'tall';

	const onButtonChange = useCallback((side) => {
		setSide(side);
	}, []);

	const onOrderTypeChange = useCallback((newValue) => {
		setCurrentOrderType(newValue);
	}, []);

	const orderTypeOptions = useMemo(() => {
		return UI_ORDER_TYPES_LIST.filter(
			(orderType) => orderType.value !== 'oracle'
		);
	}, [UI_ORDER_TYPES_LIST]);

	const onPriceBoxChange = useCallback((value) => {
		setPriceBoxStringValue(value);
	}, []);

	const onSecondaryPriceBoxChange = useCallback((value) => {
		setSecondaryPriceBoxStringValue(value);
	}, []);

	const onBaseSizeChange = useCallback(
		(value) => {
			handleFormChangeValue(value, 'base');
		},
		[stepSize]
	);

	const handleQuoteSizeChange = useCallback(
		(value) => handleFormChangeValue(value, 'quote'),
		[]
	);

	const handleToggleShowConfirmation = useCallback(() => {
		setSavedSettings({
			...savedSettings,
			showTradeConfirmation: !showTradeConfirmation,
		});
	}, [showTradeConfirmation]);

	const handleSliderChange = useCallback(
		(v) =>
			handleFormChangeValue(
				(Math.floor(v * 10000) / 10000).toFixed(2),
				'quote'
			),
		[]
	);

	const goToMarginSettings = () => {
		actions.showModal('showMarginModal');
		setShowMarginTradingMessage(false);
	};

	const saveLimitPrice = useCallback(
		(value: string) => {
			if (tradeFormState.orderType !== 'limit') return;
			set((s) => {
				s.tradeForm.savedLimitPrice = isNaN(Number(value))
					? undefined
					: {
							market: UIMarket.createSpotMarket(selectedMarket?.marketIndex),
							price: Number(value),
					  };
			});
		},
		[tradeFormState.orderType, selectedMarket?.marketIndex]
	);

	return (
		<>
			<div
				id="trade_form"
				className={`flex flex-col h-full overflow-auto thin-scroll pt-[1px] ${
					windowIsTall ? `justify-start` : `justify-between`
				}`}
			>
				<div className="flex flex-col">
					<BuySellToggle isPerp={false} onChange={onButtonChange} />
					<Utility.VERTSPACERL />
					<OrderTypeSelector
						orderType={tradeFormState.orderType}
						priceBoxStringValue={priceBoxStringValue}
						minPriceInc={minPriceInc}
						orderTypeOptions={orderTypeOptions}
						onOrderTypeChange={onOrderTypeChange}
						onPriceBoxChange={onPriceBoxChange}
						onPriceBoxSave={saveLimitPrice}
						{...tradeFormState}
					/>
					<Utility.VERTSPACERL />
					{tradeFormState.showSecondaryPriceBox && (
						<SecondaryPriceBox
							secondaryPriceBoxStringValue={secondaryPriceBoxStringValue}
							minPriceInc={minPriceInc}
							onSecondaryPriceBoxChange={onSecondaryPriceBoxChange}
						/>
					)}

					<SizeSelector
						reduceOnly={reduceOnly}
						baseSizeStringValue={UI_UTILS.roundToStepSizeIfLargeEnough(
							baseSizeStringValue,
							stepSize
						)}
						formattedMarketSymbol={formattedMarketSymbol}
						quoteSizeStringValue={quoteSizeStringValue}
						maxQuoteAvailable={tradeFormState.maxQuoteAvailable}
						maxBaseAvailableString={maxBaseSizeString}
						showMaxButton={tradeFormState.showMaxButton}
						currentPositionBazeSizeString={currentPositionBazeSizeString}
						onBaseSizeChange={onBaseSizeChange}
						handleQuoteSizeChange={handleQuoteSizeChange}
						stepSize={stepSize}
						userHasSelfImposedMax={!!maxMarginRatio && maxMarginRatio !== 0}
						orderType={tradeFormState.orderType}
					/>

					<>
						<Utility.VERTSPACERL />
						<TradeFormInputLabel
							allowPointer
							className="inline-flex items-center text-xs"
						>
							<div className="inline-flex">
								Margin Trading {userHasMarginEnabled ? 'Enabled' : 'Disabled'}
							</div>
							{!isDelegateAccount && (
								<div
									className="inline-flex hover:cursor-pointer"
									onClick={goToMarginSettings}
								>
									<Info className="ml-1" size={10} />
								</div>
							)}
						</TradeFormInputLabel>
					</>

					{tradeFormState.showLeverageSlider && userHasMarginEnabled && (
						<>
							<Utility.VERTSPACERS />
							<div className="font-display">
								<SliderButtonComboInput
									currentLeverage={currentLeverage}
									targetTradeQuoteSize={rawQuoteValue}
									totalCurrentPositionSize={totalCurrentPositionSize}
									targetMarketCurrentPositionSize={currentPositionSize}
									sameSide={
										!tradeFormState.currentPosition?.baseAssetAmount ||
										tradeFormState.currentPosition?.baseAssetAmount.eq(ZERO) ||
										(tradeFormState.currentPosition?.baseAssetAmount?.isNeg()
											? 'sell'
											: 'buy') === side
									}
									maxQuoteSize={tradeFormState.maxQuoteAvailable}
									maxLeverage={tradeFormState.maxLeverageAvailable}
									title="Leverage"
									onCurrentSizeChange={handleSliderChange}
									leverageAfterTrade={leverageAfterTrade}
									setMaxLevSelected={setMaxLevSelected}
									disabled={!connected}
								/>
							</div>
						</>
					)}

					{tradeFormState.showReducePositionSlider && userHasMarginEnabled && (
						<>
							<Utility.VERTSPACERS />
							<div className="flex justify-between w-full mb-2">
								<TradeFormInputLabel>{'Position Size (%)'}</TradeFormInputLabel>
							</div>
							<Slider
								type="percent"
								disabled={null}
								value={parseFloat(
									currentPositionBaseSize.eqZero()
										? '0'
										: rawBaseValue.toPercentage(currentPositionBaseSize, 4)
								)}
								onDrop={(val) => {
									handleFormChangeValue(
										currentPositionBaseSize.scale(val, 100).printShort(true),
										'base'
									);
								}}
								onMove={(val) => {
									handleFormChangeValue(
										currentPositionBaseSize.scale(val, 100).printShort(true),
										'base'
									);
								}}
								step={1}
								maxButtonTransition={false}
								max={100}
								min={0}
							/>
							<div className="pb-6" />
						</>
					)}

					{connected && showMarginTradingMessage && (
						<OnboardingTooltip
							content={
								<div className="items-center inline-block">
									<Text.BODY2>
										Spot Margin Trading allows you to leverage your deposits as
										collateral to borrow any asset at a variable rate. You can
										enable this later in Settings.{' '}
										<a
											href="https://docs.drift.trade/trading/spot-margin-trading"
											target="_blank"
											rel="noreferrer"
										>
											Learn more
										</a>
									</Text.BODY2>
									<div className="inline-flex items-center mt-2">
										<Button.Secondary
											className="whitespace-nowrap"
											size="MEDIUM"
											highlight
											onClick={goToMarginSettings}
										>
											Enable Spot Margin Trading
										</Button.Secondary>
										<Button.Ghost
											className="ml-1 whitespace-nowrap text-text-secondary"
											size="SMALL"
											onClick={() => setShowMarginTradingMessage(false)}
										>
											Dismiss
										</Button.Ghost>
									</div>
								</div>
							}
							visible
						>
							<SizeSelector
								reduceOnly={reduceOnly}
								baseSizeStringValue={UI_UTILS.roundToStepSizeIfLargeEnough(
									baseSizeStringValue,
									stepSize
								)}
								formattedMarketSymbol={formattedMarketSymbol}
								quoteSizeStringValue={quoteSizeStringValue}
								maxQuoteAvailable={tradeFormState.maxQuoteAvailable}
								maxBaseAvailableString={maxBaseSizeString}
								showMaxButton={tradeFormState.showMaxButton}
								currentPositionBazeSizeString={currentPositionBazeSizeString}
								onBaseSizeChange={onBaseSizeChange}
								handleQuoteSizeChange={handleQuoteSizeChange}
								stepSize={stepSize}
								userHasSelfImposedMax={!!maxMarginRatio && maxMarginRatio !== 0}
								orderType={tradeFormState.orderType}
							/>
						</OnboardingTooltip>
					)}

					{tradeFormState.showSlippageTolerance && (
						<>
							<Utility.VERTSPACERL />
							<div className="block">
								<SlippageTolerance />
							</div>
						</>
					)}

					{(tradeFormState.showReduceOnly || tradeFormState.showPostOnly) && (
						<>
							<Utility.VERTSPACERS />
							<div className="flex gap-2">
								{tradeFormState.showReduceOnly && (
									<SwitchInput
										label="Reduce Only"
										checked={reduceOnly && !postOnly}
										onChange={() => toggleReduceOnly(!reduceOnly)}
										tooltip={
											postOnly
												? postOnlyAndReduceOnlyNotAllowedTooltip
												: reduceOnlyTooltip
										}
										disabled={postOnly && tradeFormState.orderType != 'market'}
									/>
								)}
								{tradeFormState.showPostOnly && (
									<SwitchInput
										label="Post"
										checked={postOnly && !reduceOnly}
										onChange={togglePostOnly}
										tooltip={
											reduceOnly
												? postOnlyAndReduceOnlyNotAllowedTooltip
												: postOnlyTooltip
										}
										disabled={reduceOnly}
									/>
								)}
							</div>
						</>
					)}

					{tradeFormState.shouldShowMessage && (
						<>
							<div className="pb-2" />
							<InfoMessage
								type={tradeFormState.messageType}
								message={tradeFormState.message}
								link={tradeFormState.messageLink}
								linkDescription={tradeFormState.messageLinkDescription}
								messageTitle={tradeFormState.messageTitle}
								messageId={tradeFormState.messageId}
							/>

							<div className="pb-2" />
						</>
					)}
				</div>

				<div
					className={`flex flex-col justify-center ${windowIsTall && `mt-12`}`}
				>
					<AdvancedSpotTradeSettings orderType={tradeFormState.orderType} />
					<Utility.VERTSPACERS />
					<div className="flex items-left">
						<SettingsSwitch
							label="Show Confirmation"
							checked={showTradeConfirmation}
							onChange={handleToggleShowConfirmation}
							spacedOut
							includeLineSpace={false}
							className="w-full [&>span]:text-[12px]"
						/>
					</div>

					<Utility.VERTSPACERS />

					{tradeFormState.showPriceEstimateOracleDivergenceWarning &&
						!showTradeConfirmation && (
							<PriceDivergenceWarningMessage
								hasAgreedToPriceDivergence={hasAgreedToPriceDivergence}
								setHasAgreedToPriceDivergence={setHasAgreedToPriceDivergence}
							/>
						)}

					<Utility.VERTSPACERM />

					{connected ? (
						<Button.BigSemantic
							positive={side === 'buy'}
							disabled={
								tradeFormState.tradeButtonIsDisabled ||
								(tradeFormState.showPriceEstimateOracleDivergenceWarning &&
									!showTradeConfirmation &&
									!hasAgreedToPriceDivergence) ||
								(isGeoblocked && !reduceOnly) ||
								isSendingTransaction
							}
							onClick={onSubmit}
						>
							{tradeFormState.marketInSettlement ? (
								<Text.H3
									className={`flex items-center justify-center whitespace-normal ${
										currentMarketSymbol.length > 8 ? 'text-lg' : ''
									}`}
								>
									{currentMarketSymbol} IN SETTLEMENT
								</Text.H3>
							) : (
								<Text.H3
									className={`flex items-center justify-center whitespace-normal ${
										currentMarketSymbol.length > 8 ? 'text-lg' : ''
									}`}
								>
									{`${side === 'buy' ? 'BUY' : 'SELL'} ${
										rawBaseValue.gt(ZERO)
											? `${
													leadSide != 'base' ? `~` : ``
											  }${UI_UTILS.roundToStepSizeIfLargeEnough(
													baseSizeStringValue,
													stepSize
											  )}`
											: ''
									} ${currentMarketSymbol}`}
								</Text.H3>
							)}
						</Button.BigSemantic>
					) : (
						<Button.Secondary
							size="LARGE"
							onClick={onSubmit}
							disabled={isGeoblocked}
						>
							{isGeoblocked ? (
								'Unavailable'
							) : (
								<>
									<WalletIcon
										className="self-center mr-2"
										fill="var(--text-default)"
									/>
									<span className="self-center mt-0.5"> Connect</span>
								</>
							)}
						</Button.Secondary>
					)}
				</div>
			</div>

			{showTradeModal && (
				<TradeModal
					onClose={() => {
						setShowTradeModal(false);
					}}
					tradeFormMessage={
						tradeFormState.skipMessageInConfirmationModal
							? ''
							: tradeFormState.message
					}
					tradeFormMessageType={tradeFormState.messageType}
				/>
			)}
		</>
	);
};

const TradeForm = (props: { marketType: MarketType }) => {
	return isVariant(props.marketType, 'perp') ? (
		<PerpTradeForm />
	) : (
		<SpotTradeForm />
	);
};

export default React.memo(TradeForm);
